import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const CART_SECTION_REFRESH_QUERY_PARAMS = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb', 'hr'];
const CART_DRAWER_FRAGMENT_SECTION_ID = 'cart-drawer-fragment';
const CART_COUNT_REFRESH_DEBOUNCE = 40;
const CART_DRAWER_FRAGMENT_REFRESH_DEBOUNCE = 160;
const CART_DRAWER_UPSELL_FAST_PATH_MARK_PREFIX = 'cart-performance:cart-drawer-upsell-fast-add';

let cartDrawerOptimisticSequence = 0;
let cartDrawerLightweightRefreshBatchId = 0;
let cartDrawerLightweightRefreshBatch = null;

/**
 * Records stable timing marks for the cart drawer upsell fast path.
 * @param {string} step
 * @param {Record<string, string | number | boolean | undefined>} [detail]
 * @param {number} [startTime]
 */
function markCartDrawerUpsellFastPath(step, detail = {}, startTime) {
  const perf = globalThis.performance;
  if (!perf?.mark) return;

  const markName = `${CART_DRAWER_UPSELL_FAST_PATH_MARK_PREFIX}:${step}`;

  try {
    perf.mark(markName, {
      detail,
      ...(Number.isFinite(startTime) ? { startTime } : {}),
    });
  } catch (error) {
    perf.mark(markName);
  }
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = this.closest('product-form-component');
    if (productForm instanceof HTMLElement && productForm.dataset.cartDrawerUpsellAdd === 'true') {
      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';

      markCartDrawerUpsellFastPath('click', { variantId }, event.timeStamp);
    }

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #shouldSyncCartQuantity() {
    return this.dataset.cartQuantitySync !== 'false';
  }

  /**
   * Gets cart section IDs currently registered on the page.
   * @returns {string[]}
   */
  #getCartItemComponentsSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }

  /**
   * Adds cart section rendering params to a cart add form payload.
   * @param {FormData} formData
   * @param {string[]} sectionIds
   */
  #appendCartSectionsToFormData(formData, sectionIds) {
    if (!sectionIds.length) return;

    formData.append('sections', sectionIds.join(','));
  }

  /**
   * Checks whether this form can use the scoped lightweight cart add path.
   * @param {HTMLFormElement} form
   * @param {string[]} sectionIds
   * @returns {boolean}
   */
  #shouldUseLightweightCartAdd(form, sectionIds) {
    const quickAddComponent = this.closest('quick-add-component');
    const cartDrawerComponent = this.closest('cart-drawer-component');
    const cartItemsComponent = this.closest('cart-items-component');
    const hasQuantitySelector = !!this.querySelector('quantity-selector-component');
    const hasQuantityLabel = !!this.refs.quantityLabelCartCount || !!this.querySelector('[ref="quantityLabelCartCount"]');
    const cartDrawerCanAutoOpen = !!document.querySelector('cart-drawer-component[auto-open]');
    const isProductGridDirectAdd =
      this.dataset.productGridDirectAdd === 'true' &&
      quickAddComponent instanceof HTMLElement &&
      quickAddComponent.dataset.quickAddButton === 'add';
    const isCartDrawerUpsellAdd =
      this.dataset.cartDrawerUpsellAdd === 'true' &&
      this.classList.contains('cart-drawer__upsell-form') &&
      cartDrawerComponent instanceof HTMLElement &&
      cartItemsComponent instanceof HTMLElement &&
      this.closest('[data-cart-drawer-upsell]') instanceof HTMLElement &&
      !this.closest('quick-add-component, quick-add-dialog');

    const hasLightweightFormShape =
      form.matches('[data-type="add-to-cart-form"]') &&
      sectionIds.length > 0 &&
      !hasQuantitySelector &&
      !hasQuantityLabel;

    if (!hasLightweightFormShape) return false;

    if (isProductGridDirectAdd) {
      return cartDrawerCanAutoOpen && !this.closest('quick-add-dialog, cart-items-component');
    }

    return isCartDrawerUpsellAdd;
  }

  /**
   * Checks whether this form should bypass the general product-form submit path.
   * @param {HTMLFormElement} form
   * @param {string[]} sectionIds
   * @returns {boolean}
   */
  #shouldUseCartDrawerUpsellFastAdd(form, sectionIds) {
    return this.dataset.cartDrawerUpsellAdd === 'true' && this.#shouldUseLightweightCartAdd(form, sectionIds);
  }

  /**
   * Builds a section refresh URL without copying tracking or recommendation params.
   * @param {string} sectionId
   * @returns {URL}
   */
  #buildCartSectionRefreshUrl(sectionId) {
    const url = new URL(window.location.pathname || '/', window.location.origin);
    const currentUrl = new URL(window.location.href);

    for (const key of CART_SECTION_REFRESH_QUERY_PARAMS) {
      const value = currentUrl.searchParams.get(key);

      if (value) url.searchParams.set(key, value);
    }

    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();

    return url;
  }

  /**
   * Checks whether the lightweight drawer fragment can update the current drawer safely.
   * @returns {boolean}
   */
  #canUseCartDrawerFragmentRefresh() {
    const drawerDialog = document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');

    return drawerDialog instanceof HTMLElement && !drawerDialog.classList.contains('cart-drawer--empty');
  }

  /**
   * Fetches rendered cart sections after a lightweight add.
   * @param {string[]} sectionIds
   * @param {AbortSignal} [signal]
   * @returns {Promise<Record<string, string>>}
   */
  async #fetchCartSectionsForLightweightAdd(sectionIds, signal) {
    if (this.#canUseCartDrawerFragmentRefresh()) {
      try {
        const fragmentResponse = await fetch(this.#buildCartSectionRefreshUrl(CART_DRAWER_FRAGMENT_SECTION_ID), {
          credentials: 'same-origin',
          signal,
          headers: {
            Accept: 'text/html',
          },
        });

        if (!fragmentResponse.ok) {
          throw new Error(`Cart drawer fragment refresh failed with status ${fragmentResponse.status}`);
        }

        const fragmentHtml = await fragmentResponse.text();

        if (this.#isValidCartDrawerFragment(fragmentHtml)) {
          return {
            [CART_DRAWER_FRAGMENT_SECTION_ID]: fragmentHtml,
          };
        }

        throw new Error('Cart drawer fragment response was missing required drawer markup');
      } catch (error) {
        console.warn('Falling back to full cart section refresh after lightweight add:', error);
      }
    }

    const sections = await Promise.all(
      sectionIds.map(async (sectionId) => {
        const response = await fetch(this.#buildCartSectionRefreshUrl(sectionId), {
          credentials: 'same-origin',
          signal,
          headers: {
            Accept: 'text/html',
          },
        });

        if (!response.ok) throw new Error(`Cart section refresh failed with status ${response.status}`);

        return [sectionId, await response.text()];
      })
    );

    return Object.fromEntries(sections);
  }

  /**
   * Checks that the drawer fragment has the minimum markup needed for a safe cart update.
   * @param {string} fragmentHtml
   * @returns {boolean}
   */
  #isValidCartDrawerFragment(fragmentHtml) {
    const fragment = new DOMParser().parseFromString(fragmentHtml, 'text/html');
    const drawerFragment = fragment.querySelector('[data-cart-drawer-fragment]');
    const cartItemsComponent = drawerFragment?.querySelector('cart-items-component');
    const cartItemCount = drawerFragment?.querySelector('[ref="cartItemCount"]');
    const isEmpty = drawerFragment?.getAttribute('data-cart-drawer-empty') === 'true';

    return !!drawerFragment && !!cartItemsComponent && (isEmpty || !!cartItemCount);
  }

  /**
   * Reads the rendered total cart item count from refreshed sections.
   * @param {Record<string, string>} sections
   * @param {string[]} sectionIds
   * @returns {number | null}
   */
  #getRenderedCartItemCount(sections, sectionIds) {
    const parser = new DOMParser();

    for (const sectionId of new Set([...Object.keys(sections), ...sectionIds])) {
      const sectionHtml = sections[sectionId];
      if (!sectionHtml) continue;

      const parsedSection = parser.parseFromString(sectionHtml, 'text/html');
      const countText = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      const count = countText ? parseInt(countText, 10) : NaN;

      if (Number.isFinite(count)) return count;
    }

    return null;
  }

  /**
   * Reads the current visible cart count after the optimistic cart add event.
   * @returns {number}
   */
  #getCurrentVisibleCartItemCount() {
    const countText = document.querySelector('cart-icon [ref="cartBubbleCount"]')?.textContent?.trim();
    const count = countText ? parseInt(countText, 10) : 0;

    return Number.isFinite(count) ? count : 0;
  }

  /**
   * Normalizes cart item count fields from JSON or event payloads.
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #normalizeCartItemCount(value, fallback) {
    const count = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);

    return Number.isFinite(count) && count >= 0 ? count : fallback;
  }

  /**
   * Reads the minimal payload needed for a cart drawer upsell add.
   * @param {HTMLFormElement} form
   * @returns {{ id: string, quantity: number } | null}
   */
  #getCartDrawerUpsellFastAddPayload(form) {
    const variantInput = this.refs.variantId;

    if (!(variantInput instanceof HTMLInputElement) || variantInput.disabled || !variantInput.value) return null;

    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantityValue = quantityInput instanceof HTMLInputElement ? quantityInput.value : this.dataset.quantityDefault;
    const quantity = Math.max(1, parseInt(String(quantityValue || '1'), 10) || 1);

    return {
      id: variantInput.value,
      quantity,
    };
  }

  /**
   * Reads text intended for sighted cart UI, excluding screen-reader labels.
   * @param {Element | null} element
   * @returns {string}
   */
  #getVisibleElementText(element) {
    if (!(element instanceof Element)) return '';

    const clone = element.cloneNode(true);
    if (!(clone instanceof Element)) return '';

    clone.querySelectorAll('.visually-hidden, [hidden], [aria-hidden="true"]').forEach((node) => node.remove());

    return clone.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  /**
   * Reads enough rendered upsell-card data to show an immediate optimistic row in the drawer.
   * @param {HTMLFormElement} form
   * @param {{ id: string, quantity: number }} payload
   * @returns {{ variantId: string, productId: string | undefined, quantity: number, title: string, url: string, imageSrc: string, imageAlt: string, priceText: string, statusText: string } | null}
   */
  #getCartDrawerUpsellOptimisticLineItem(form, payload) {
    const item = this.closest('[data-cart-drawer-upsell-item]');
    if (!(item instanceof HTMLElement)) return null;

    const titleLink = item.querySelector('.cart-drawer__upsell-title');
    const image = item.querySelector('.cart-drawer__upsell-image');
    const price = item.querySelector('.cart-drawer__upsell-price');
    const salePrice = item.querySelector('.cart-drawer__upsell-sale-price');
    const title = titleLink?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const priceText = this.#getVisibleElementText(salePrice) || this.#getVisibleElementText(price);

    return {
      variantId: payload.id,
      productId: this.dataset.productId,
      quantity: payload.quantity,
      title,
      url: titleLink instanceof HTMLAnchorElement ? titleLink.href : '',
      imageSrc: image instanceof HTMLImageElement ? image.currentSrc || image.src : '',
      imageAlt: image instanceof HTMLImageElement ? image.alt || title : title,
      priceText,
      statusText: Theme.translations?.adding_to_cart || 'Adding...',
    };
  }

  /**
   * Creates a tokened optimistic count state for a product-grid drawer refresh.
   * @param {{ itemCount: number, variantId: string }} cartAddData
   * @param {string | null} [batchToken]
   * @returns {{ token: string, sequence: number, itemCount: number }}
   */
  #createCartDrawerOptimisticState(cartAddData, batchToken = null) {
    const sequence = ++cartDrawerOptimisticSequence;
    const addedItemCount = Number(cartAddData.itemCount) || 0;
    const optimisticItemCount = Math.max(0, this.#getCurrentVisibleCartItemCount() + addedItemCount);

    return {
      token: batchToken || `${cartAddData.variantId}:${sequence}`,
      sequence,
      itemCount: optimisticItemCount,
    };
  }

  /**
   * Adds cart drawer optimistic reconciliation fields to a cart event payload.
   * @param {{ token: string, sequence: number, itemCount: number } | null} optimisticState
   * @returns {Record<string, string | number>}
   */
  #cartDrawerOptimisticEventData(optimisticState) {
    if (!optimisticState) return {};

    return {
      cartDrawerOptimisticToken: optimisticState.token,
      cartDrawerOptimisticSequence: optimisticState.sequence,
      optimisticItemCount: optimisticState.itemCount,
    };
  }

  /**
   * Starts or joins the current product-grid add batch.
   * @param {string[]} sectionIds
   * @returns {{ id: number, token: string }}
   */
  #beginLightweightCartAddBatch(sectionIds) {
    if (!cartDrawerLightweightRefreshBatch) {
      const id = ++cartDrawerLightweightRefreshBatchId;

      cartDrawerLightweightRefreshBatch = {
        id,
        token: `product-grid-cart-drawer:${id}`,
        pendingRequests: 0,
        successfulAdds: 0,
        sectionIds: new Set(),
        owner: this,
        sourceId: '',
        cartAddData: null,
        optimisticState: null,
        refreshTimer: null,
        abortController: null,
        refreshGeneration: 0,
        countRefreshTimer: null,
        countAbortController: null,
        countRefreshGeneration: 0,
      };
    }

    const batch = cartDrawerLightweightRefreshBatch;

    batch.pendingRequests += 1;
    batch.owner = this;
    sectionIds.forEach((sectionId) => batch.sectionIds.add(sectionId));

    if (batch.refreshTimer) {
      window.clearTimeout(batch.refreshTimer);
      batch.refreshTimer = null;
    }

    if (batch.countRefreshTimer) {
      window.clearTimeout(batch.countRefreshTimer);
      batch.countRefreshTimer = null;
    }

    if (batch.abortController) {
      batch.abortController.abort();
      batch.abortController = null;
      batch.refreshGeneration += 1;
    }

    if (batch.countAbortController) {
      batch.countAbortController.abort();
      batch.countAbortController = null;
      batch.countRefreshGeneration += 1;
    }

    return {
      id: batch.id,
      token: batch.token,
    };
  }

  /**
   * Records a successful product-grid add without starting an immediate fragment refresh.
   * @param {number} batchId
   * @param {string} sourceId
   * @param {{ itemCount: number, productId: string | undefined, variantId: string }} cartAddData
   * @param {string[]} sectionIds
   * @param {{ token: string, sequence: number, itemCount: number }} optimisticState
   */
  #recordLightweightCartAddSuccess(batchId, sourceId, cartAddData, sectionIds, optimisticState) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch) return;

    batch.successfulAdds += 1;
    batch.owner = this;
    batch.sourceId = sourceId;
    batch.cartAddData = { ...cartAddData };
    batch.optimisticState = optimisticState;
    sectionIds.forEach((sectionId) => batch.sectionIds.add(sectionId));
  }

  /**
   * Completes one product-grid add request and schedules the final refresh when the batch has settled.
   * @param {number} batchId
   */
  #completeLightweightCartAddRequest(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch) return;

    batch.pendingRequests = Math.max(0, batch.pendingRequests - 1);
    this.#scheduleLightweightCartCountReconcile(batchId);
    this.#scheduleLightweightCartSectionRefresh(batchId);
  }

  /**
   * Schedules a tiny authoritative count refresh for the current settled product-grid add batch.
   * @param {number} batchId
   */
  #scheduleLightweightCartCountReconcile(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch) return;

    if (batch.countRefreshTimer) {
      window.clearTimeout(batch.countRefreshTimer);
      batch.countRefreshTimer = null;
    }

    if (batch.pendingRequests > 0 || batch.successfulAdds <= 0) return;

    const owner = batch.owner || this;

    batch.countRefreshTimer = window.setTimeout(() => {
      batch.countRefreshTimer = null;
      owner.#dispatchCoalescedLightweightCartCountReconcile(batchId);
    }, CART_COUNT_REFRESH_DEBOUNCE);
  }

  /**
   * Dispatches the authoritative count-only update independently of the heavier drawer content refresh.
   * @param {number} batchId
   * @returns {Promise<void>}
   */
  async #dispatchCoalescedLightweightCartCountReconcile(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch || batch.pendingRequests > 0 || batch.successfulAdds <= 0 || !batch.cartAddData) return;

    const countRefreshGeneration = ++batch.countRefreshGeneration;
    const sourceId = batch.sourceId || batch.cartAddData.variantId;
    const optimisticState = batch.optimisticState;

    batch.countAbortController = new AbortController();

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        signal: batch.countAbortController.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Cart count refresh failed with status ${response.status}`);

      const cart = await response.json();
      if (!this.#isCurrentLightweightCartAddCountBatch(batchId, countRefreshGeneration)) return;

      const itemCount = this.#normalizeCartItemCount(cart?.item_count, this.#getCurrentVisibleCartItemCount());

      this.#dispatchLightweightCartCountUpdate(sourceId, batch.cartAddData, itemCount, optimisticState, {
        cartCountAuthoritative: true,
        cartDrawerOptimisticFinal: true,
        cartDrawerCoalescedAddCount: batch.successfulAdds,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('Cart count reconcile deferred to drawer content refresh:', error);
      }
    } finally {
      if (this.#isCurrentLightweightCartAddCountBatch(batchId, countRefreshGeneration)) {
        batch.countAbortController = null;
      }
    }
  }

  /**
   * Schedules one authoritative drawer refresh for the current settled product-grid add batch.
   * @param {number} batchId
   */
  #scheduleLightweightCartSectionRefresh(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch) return;

    if (batch.refreshTimer) {
      window.clearTimeout(batch.refreshTimer);
      batch.refreshTimer = null;
    }

    if (batch.pendingRequests > 0) return;

    if (batch.successfulAdds <= 0) {
      this.#finishLightweightCartAddBatch(batchId);
      return;
    }

    const owner = batch.owner || this;

    batch.refreshTimer = window.setTimeout(() => {
      batch.refreshTimer = null;
      owner.#dispatchCoalescedLightweightCartSectionRefresh(batchId);
    }, CART_DRAWER_FRAGMENT_REFRESH_DEBOUNCE);
  }

  /**
   * Dispatches the coalesced follow-up rendered section update after rapid lightweight adds settle.
   * @param {number} batchId
   * @returns {Promise<void>}
   */
  async #dispatchCoalescedLightweightCartSectionRefresh(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch || batch.pendingRequests > 0 || batch.successfulAdds <= 0 || !batch.cartAddData) return;

    const refreshGeneration = ++batch.refreshGeneration;
    const sectionIds = Array.from(batch.sectionIds);
    const sourceId = batch.sourceId || batch.cartAddData.variantId;
    const optimisticState = batch.optimisticState;

    batch.abortController = new AbortController();

    try {
      const sections = await this.#fetchCartSectionsForLightweightAdd(sectionIds, batch.abortController.signal);
      if (!this.#isCurrentLightweightCartAddBatch(batchId, refreshGeneration)) return;

      const itemCount = this.#getRenderedCartItemCount(sections, sectionIds) ?? this.#getCurrentVisibleCartItemCount();
      const didUseDrawerFragment = !!sections[CART_DRAWER_FRAGMENT_SECTION_ID];

      this.dispatchEvent(
        new CartAddEvent({}, sourceId, {
          ...batch.cartAddData,
          source: didUseDrawerFragment ? 'cart-drawer-fragment-refresh' : 'cart-section-refresh',
          itemCount,
          sections,
          cartDrawerFragmentRefresh: didUseDrawerFragment,
          lightweightSectionRefresh: true,
          skipCartDrawerMirrorRefresh: didUseDrawerFragment,
          cartDrawerOptimisticFinal: true,
          cartDrawerCoalescedRefresh: true,
          cartDrawerCoalescedAddCount: batch.successfulAdds,
          ...this.#cartDrawerOptimisticEventData(optimisticState),
        })
      );

      if (batch.cartAddData.source === 'cart-drawer-upsell-fast-add') {
        markCartDrawerUpsellFastPath('drawer-content-final-refresh', {
          addCount: batch.successfulAdds,
          batchId,
          itemCount,
          refreshGeneration,
          usedDrawerFragment: didUseDrawerFragment,
        });
      }

      this.#queueCartDrawerMirrorRefresh(sourceId, batch.cartAddData, itemCount, optimisticState);
      this.#finishLightweightCartAddBatch(batchId);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('Failed to refresh cart drawer after lightweight add:', error);
      if (!this.#isCurrentLightweightCartAddBatch(batchId, refreshGeneration)) return;

      this.dispatchEvent(
        new CartAddEvent({}, sourceId, {
          ...batch.cartAddData,
          source: 'cart-section-refresh',
          itemCount: this.#getCurrentVisibleCartItemCount(),
          lightweightSectionRefreshFailed: true,
          cartDrawerOptimisticFinal: true,
          cartDrawerOptimisticFailed: true,
          cartDrawerCoalescedRefresh: true,
          cartDrawerCoalescedAddCount: batch.successfulAdds,
          ...this.#cartDrawerOptimisticEventData(optimisticState),
        })
      );

      this.#finishLightweightCartAddBatch(batchId);
    }
  }

  /**
   * Gets the active lightweight add batch when the ID still matches.
   * @param {number} batchId
   * @returns {any | null}
   */
  #getLightweightCartAddBatch(batchId) {
    if (!cartDrawerLightweightRefreshBatch || cartDrawerLightweightRefreshBatch.id !== batchId) return null;

    return cartDrawerLightweightRefreshBatch;
  }

  /**
   * Checks that an async refresh still belongs to the active batch generation.
   * @param {number} batchId
   * @param {number} refreshGeneration
   * @returns {boolean}
   */
  #isCurrentLightweightCartAddBatch(batchId, refreshGeneration) {
    const batch = this.#getLightweightCartAddBatch(batchId);

    return !!batch && batch.refreshGeneration === refreshGeneration;
  }

  /**
   * Checks that an async count refresh still belongs to the active batch generation.
   * @param {number} batchId
   * @param {number} countRefreshGeneration
   * @returns {boolean}
   */
  #isCurrentLightweightCartAddCountBatch(batchId, countRefreshGeneration) {
    const batch = this.#getLightweightCartAddBatch(batchId);

    return !!batch && batch.countRefreshGeneration === countRefreshGeneration;
  }

  /**
   * Clears the active lightweight add batch after its final state has reconciled.
   * @param {number} batchId
   */
  #finishLightweightCartAddBatch(batchId) {
    const batch = this.#getLightweightCartAddBatch(batchId);
    if (!batch) return;

    if (batch.refreshTimer) {
      window.clearTimeout(batch.refreshTimer);
    }

    if (batch.countRefreshTimer) {
      window.clearTimeout(batch.countRefreshTimer);
    }

    batch.abortController?.abort();
    batch.countAbortController?.abort();
    cartDrawerLightweightRefreshBatch = null;
  }

  /**
   * Dispatches a count-only cart update that cannot trigger drawer content or mirror refreshes.
   * @param {string} sourceId
   * @param {{ itemCount: number, productId: string | undefined, variantId: string }} cartAddData
   * @param {number} itemCount
   * @param {{ token: string, sequence: number, itemCount: number } | null} optimisticState
   * @param {Record<string, any>} [extraData]
   */
  #dispatchLightweightCartCountUpdate(sourceId, cartAddData, itemCount, optimisticState, extraData = {}) {
    this.dispatchEvent(
      new CartAddEvent({}, sourceId, {
        ...cartAddData,
        source: 'cart-count-refresh',
        itemCount,
        cartCountUpdateOnly: true,
        lightweightCartCountUpdate: true,
        ...this.#cartDrawerOptimisticEventData(optimisticState),
        ...extraData,
      })
    );
  }

  /**
   * Queues a cart update that lets the drawer refresh deferred mirrored blocks after rendered slots exist.
   * @param {string} sourceId
   * @param {{ itemCount: number, productId: string | undefined, variantId: string }} cartAddData
   * @param {number} itemCount
   * @param {{ token: string, sequence: number, itemCount: number } | null} optimisticState
   */
  #queueCartDrawerMirrorRefresh(sourceId, cartAddData, itemCount, optimisticState = null) {
    requestAnimationFrame(() => {
      this.dispatchEvent(
        new CartAddEvent({}, sourceId, {
          ...cartAddData,
          source: 'cart-drawer-mirror-refresh',
          itemCount,
          cartDrawerMirrorRefreshOnly: true,
          ...this.#cartDrawerOptimisticEventData(optimisticState),
        })
      );
    });
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    if (!this.#shouldSyncCartQuantity()) return 0;

    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component' ||
      event.detail?.data?.cartCountUpdateOnly ||
      event.detail?.data?.lightweightSectionRefreshPending ||
      event.detail?.data?.lightweightSectionRefreshFailed ||
      event.detail?.data?.lightweightSectionRefresh ||
      event.detail?.data?.cartDrawerOptimisticFinal ||
      event.detail?.data?.cartDrawerMirrorRefreshOnly
    ) {
      return;
    }

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Updates the busy state for a cart drawer upsell fast-path button without blocking rapid adds.
   * @param {boolean} isPending
   */
  #setCartDrawerUpsellFastButtonPending(isPending) {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const currentPendingCount = this.#normalizeCartItemCount(button.dataset.cartDrawerUpsellPendingCount, 0);
    const nextPendingCount = Math.max(0, currentPendingCount + (isPending ? 1 : -1));

    if (nextPendingCount > 0) {
      button.dataset.cartDrawerUpsellPendingCount = String(nextPendingCount);
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('data-cart-drawer-upsell-add-pending', 'true');
      return;
    }

    delete button.dataset.cartDrawerUpsellPendingCount;
    button.removeAttribute('aria-busy');
    button.removeAttribute('data-cart-drawer-upsell-add-pending');
  }

  /**
   * Announces a successful cart drawer upsell add through the existing live region.
   */
  #announceCartDrawerUpsellFastAddSuccess() {
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const addedTextElement = addToCartButton?.querySelector('.add-to-cart-text--added');
    const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

    this.#setLiveRegionText(addedText);

    setTimeout(() => {
      this.#clearLiveRegionText();
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Clears optimistic button success styling after a failed fast-path add.
   */
  #clearCartDrawerUpsellFastButtonAddedState() {
    this.refs.addToCartButtonContainer?.refs.addToCartButton?.classList.remove('atc-added');
  }

  /**
   * Displays an accessible error for the cart drawer upsell fast path.
   * @param {HTMLFormElement} form
   * @param {{ message?: string, description?: string, errors?: any }} response
   */
  #handleCartDrawerUpsellFastAddError(form, response) {
    const { addToCartTextError } = this.refs;
    const errorMessage =
      response.message || response.description || Theme.translations.add_to_cart_error || 'Unable to add item to cart.';

    this.#clearCartDrawerUpsellFastButtonAddedState();

    this.dispatchEvent(
      new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
    );

    this.#setLiveRegionText(errorMessage);

    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);

    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = errorMessage;
    } else {
      addToCartTextError.appendChild(document.createTextNode(errorMessage));
    }
  }

  /**
   * Handles cart drawer upsell adds without the general product form FormData/section path.
   * @param {Event} event
   * @param {HTMLFormElement} form
   * @param {string[]} cartItemComponentsSectionIds
   */
  #handleCartDrawerUpsellFastSubmit(event, form, cartItemComponentsSectionIds) {
    const payload = this.#getCartDrawerUpsellFastAddPayload(form);
    if (!payload) return;

    const optimisticLineItem = this.#getCartDrawerUpsellOptimisticLineItem(form, payload);
    const optimisticLineItemData = optimisticLineItem ? { optimisticLineItem } : {};
    const cartAddData = {
      source: 'cart-drawer-upsell-fast-add',
      itemCount: payload.quantity,
      productId: this.dataset.productId,
      variantId: payload.id,
    };
    const lightweightCartAddBatch = this.#beginLightweightCartAddBatch(cartItemComponentsSectionIds);
    const previousVisibleCartItemCount = this.#getCurrentVisibleCartItemCount();
    const optimisticState = this.#createCartDrawerOptimisticState(cartAddData, lightweightCartAddBatch.token);
    let didRollbackOptimisticUpdate = false;

    const rollbackOptimisticUpdate = () => {
      if (didRollbackOptimisticUpdate) return;

      this.#dispatchLightweightCartCountUpdate(payload.id, cartAddData, previousVisibleCartItemCount, optimisticState, {
        cartCountAuthoritative: true,
        cartDrawerOptimisticFinal: true,
        cartDrawerOptimisticFailed: true,
        cartDrawerPreserveContentPending: true,
        cartDrawerUpsellFastPath: true,
        ...optimisticLineItemData,
      });

      didRollbackOptimisticUpdate = true;
    };

    this.#setCartDrawerUpsellFastButtonPending(true);

    this.#dispatchLightweightCartCountUpdate(payload.id, cartAddData, optimisticState.itemCount, optimisticState, {
      cartCountOptimisticPending: true,
      cartDrawerOptimisticPending: true,
      cartDrawerPreserveContentPending: true,
      cartDrawerUpsellFastPath: true,
      ...optimisticLineItemData,
    });
    markCartDrawerUpsellFastPath('optimistic-count-update', {
      batchId: lightweightCartAddBatch.id,
      itemCount: optimisticState.itemCount,
      sequence: optimisticState.sequence,
      variantId: payload.id,
    });

    this.dispatchEvent(
      new CartAddEvent({}, payload.id, {
        ...cartAddData,
        lightweightSectionRefreshPending: true,
        cartDrawerContentRefreshPending: true,
        cartDrawerOptimisticPending: true,
        cartDrawerPreserveContentPending: true,
        cartDrawerUpsellFastPath: true,
        ...optimisticLineItemData,
        ...this.#cartDrawerOptimisticEventData(optimisticState),
      })
    );

    markCartDrawerUpsellFastPath('add-request-start', {
      batchId: lightweightCartAddBatch.id,
      quantity: payload.quantity,
      sequence: optimisticState.sequence,
      variantId: payload.id,
    });

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: payload.id,
        quantity: payload.quantity,
      }),
    })
      .then((response) => {
        markCartDrawerUpsellFastPath('add-response', {
          batchId: lightweightCartAddBatch.id,
          ok: response.ok,
          sequence: optimisticState.sequence,
          status: response.status,
          variantId: payload.id,
        });

        return response.json();
      })
      .then((response) => {
        if (response.status) {
          rollbackOptimisticUpdate();
          this.#handleCartDrawerUpsellFastAddError(form, response);

          this.dispatchEvent(
            new CartAddEvent({}, payload.id, {
              ...cartAddData,
              didError: true,
              cartDrawerOptimisticFinal: true,
              cartDrawerOptimisticFailed: true,
              cartDrawerUpsellFastPath: true,
              ...optimisticLineItemData,
              ...this.#cartDrawerOptimisticEventData(optimisticState),
            })
          );

          return;
        }

        this.#announceCartDrawerUpsellFastAddSuccess();
        this.#recordLightweightCartAddSuccess(
          lightweightCartAddBatch.id,
          payload.id,
          cartAddData,
          cartItemComponentsSectionIds,
          optimisticState
        );
      })
      .catch((error) => {
        console.error(error);
        rollbackOptimisticUpdate();
        this.#handleCartDrawerUpsellFastAddError(form, {
          message: Theme.translations.add_to_cart_error || 'Unable to add item to cart.',
          description: '',
          errors: error,
        });
      })
      .finally(() => {
        this.#setCartDrawerUpsellFastButtonPending(false);
        this.#completeLightweightCartAddRequest(lightweightCartAddBatch.id);
        cartPerformance.measureFromEvent('cart-drawer-upsell-fast-add:user-action', event);
      });
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const cartItemComponentsSectionIds = this.#getCartItemComponentsSectionIds();

    if (this.#shouldUseCartDrawerUpsellFastAdd(form, cartItemComponentsSectionIds)) {
      this.#handleCartDrawerUpsellFastSubmit(event, form, cartItemComponentsSectionIds);
      return;
    }

    const formData = new FormData(form);
    const submittedVariantId = formData.get('id')?.toString();
    const cartAddData = submittedVariantId
      ? {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          productId: this.dataset.productId,
          variantId: submittedVariantId,
        }
      : null;

    const shouldUseLightweightCartAdd = this.#shouldUseLightweightCartAdd(form, cartItemComponentsSectionIds);
    const lightweightCartAddBatch = shouldUseLightweightCartAdd
      ? this.#beginLightweightCartAddBatch(cartItemComponentsSectionIds)
      : null;
    const shouldPreserveCartDrawerPendingContent =
      shouldUseLightweightCartAdd && this.dataset.cartDrawerUpsellAdd === 'true';
    const previousVisibleCartItemCount = this.#getCurrentVisibleCartItemCount();
    let lightweightOptimisticState = null;
    let didDispatchLightweightOptimisticPending = false;

    if (shouldPreserveCartDrawerPendingContent && cartAddData && submittedVariantId) {
      lightweightOptimisticState = this.#createCartDrawerOptimisticState(cartAddData, lightweightCartAddBatch?.token);

      this.#dispatchLightweightCartCountUpdate(
        submittedVariantId,
        cartAddData,
        lightweightOptimisticState.itemCount,
        lightweightOptimisticState,
        {
          cartCountOptimisticPending: true,
          cartDrawerOptimisticPending: true,
          cartDrawerPreserveContentPending: true,
        }
      );

      this.dispatchEvent(
        new CartAddEvent({}, submittedVariantId, {
          ...cartAddData,
          lightweightSectionRefreshPending: true,
          cartDrawerContentRefreshPending: true,
          cartDrawerOptimisticPending: true,
          cartDrawerPreserveContentPending: true,
          ...this.#cartDrawerOptimisticEventData(lightweightOptimisticState),
        })
      );

      didDispatchLightweightOptimisticPending = true;
    }

    const rollbackImmediateLightweightOptimisticUpdate = () => {
      if (
        !didDispatchLightweightOptimisticPending ||
        !submittedVariantId ||
        !cartAddData ||
        !lightweightOptimisticState
      ) {
        return;
      }

      this.#dispatchLightweightCartCountUpdate(
        submittedVariantId,
        cartAddData,
        previousVisibleCartItemCount,
        lightweightOptimisticState,
        {
          cartCountAuthoritative: true,
          cartDrawerOptimisticFinal: true,
          cartDrawerOptimisticFailed: true,
          cartDrawerPreserveContentPending: true,
        }
      );

      didDispatchLightweightOptimisticPending = false;
    };

    if (!shouldUseLightweightCartAdd) {
      this.#appendCartSectionsToFormData(formData, cartItemComponentsSectionIds);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          const errorMessage = response.message || response.description || '';
          this.#setLiveRegionText(errorMessage);

          this.#timeout = setTimeout(() => {
            addToCartTextError?.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          rollbackImmediateLightweightOptimisticUpdate();

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!submittedVariantId || !cartAddData) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          if (shouldUseLightweightCartAdd) {
            const optimisticState =
              lightweightOptimisticState ||
              this.#createCartDrawerOptimisticState(cartAddData, lightweightCartAddBatch?.token);

            if (!didDispatchLightweightOptimisticPending) {
              this.#dispatchLightweightCartCountUpdate(
                submittedVariantId,
                cartAddData,
                optimisticState.itemCount,
                optimisticState,
                {
                  cartCountOptimisticPending: true,
                  cartDrawerOptimisticPending: true,
                }
              );
            }

            this.#recordLightweightCartAddSuccess(
              lightweightCartAddBatch?.id || 0,
              submittedVariantId,
              cartAddData,
              cartItemComponentsSectionIds,
              optimisticState
            );

            if (!didDispatchLightweightOptimisticPending) {
              this.dispatchEvent(
                new CartAddEvent({}, submittedVariantId, {
                  ...cartAddData,
                  lightweightSectionRefreshPending: true,
                  cartDrawerContentRefreshPending: true,
                  cartDrawerOptimisticPending: true,
                  ...this.#cartDrawerOptimisticEventData(optimisticState),
                })
              );
            }

            return;
          }

          // Fetch the updated cart to get the actual total quantity for this variant.
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, submittedVariantId, {
              ...cartAddData,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        rollbackImmediateLightweightOptimisticUpdate();
      })
      .finally(() => {
        if (lightweightCartAddBatch) this.#completeLightweightCartAddRequest(lightweightCartAddBatch.id);
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
